import React from "react";
import { color, radius, space, text, weight, shadow, zIndex } from "../tokens";

/**
 * <Tooltip> — small dark overlay on hover.
 *
 *   content  : ReactNode shown in the tooltip
 *   side     : "top" default | "bottom" | "left" | "right"
 *   delay    : ms before showing (default 200)
 *   children : the target element to wrap
 *
 * Uses a pure-CSS positioned span (no portal). For rich content, use a
 * Popover instead — this primitive is optimized for short labels.
 */
export function Tooltip({ content, side = "top", delay = 200, children, style }) {
  const [visible, setVisible] = React.useState(false);
  const timer = React.useRef(null);

  const show = () => {
    timer.current = setTimeout(() => setVisible(true), delay);
  };
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    setVisible(false);
  };

  const positionStyle = {
    top:    { bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)" },
    bottom: { top:    "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)" },
    left:   { right:  "calc(100% + 6px)", top:  "50%", transform: "translateY(-50%)" },
    right:  { left:   "calc(100% + 6px)", top:  "50%", transform: "translateY(-50%)" },
  }[side];

  return (
    <span
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      style={{ position: "relative", display: "inline-block", ...style }}
    >
      {children}
      {visible && content && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            zIndex: zIndex.tooltip,
            background: color.bg.inverse,
            color: color.text.inverse,
            fontSize: text.xs,
            fontWeight: weight.medium,
            padding: `${space[2]}px ${space[3]}px`,
            borderRadius: radius.md,
            boxShadow: shadow.md,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            ...positionStyle,
          }}
        >
          {content}
        </span>
      )}
    </span>
  );
}
